// A ticketed booking's choice rules, shared by the guest widget's panel and the guide's New Booking:
// total guests shared out between ticket types, and the places left at the chosen start time
// (useTicketedAvailability).
import { getGuestLimitPerBooking } from "utils/experience"

export const countTickets = (ticketTypes, ticketCounts) =>
  ticketTypes.reduce((sum, ticketType) => sum + Number(ticketCounts?.[ticketType.id] || 0), 0)

// Lowering the guest count below the tickets already chosen takes tickets back, last type first.
export const trimTicketCounts = (ticketTypes, ticketCounts, totalGuests) => {
  let excess = countTickets(ticketTypes, ticketCounts) - totalGuests
  if (excess <= 0) return ticketCounts
  const next = { ...ticketCounts }
  for (const ticketType of [...ticketTypes].reverse()) {
    const removed = Math.min(excess, Number(next[ticketType.id] || 0))
    next[ticketType.id] = Number(next[ticketType.id] || 0) - removed
    excess -= removed
    if (excess === 0) break
  }
  return next
}

// Each start time's chip note by label: sold out, or its places left once they reach the trip's
// low-availability threshold. Start times without places from the API get none.
export const getSlotStatus = (experience, slots) => {
  const threshold = experience?.low_availability_enabled ? Number(experience.low_availability_threshold) || 0 : 0
  return Object.fromEntries(
    slots
      .filter((slot) => slot.remaining != null)
      .map((slot) => [
        slot.label,
        { isSoldOut: slot.isSoldOut, placesLeft: !slot.isSoldOut && slot.remaining <= threshold ? slot.remaining : null }
      ])
  )
}

// "Availability has changed: ..." when the choice no longer fits the places left (design 6058:136613
// and 6058:136714), else null.
export const getAvailabilityWarning = ({ t, selectedSlot, isDraftSlotGone, totalGuests, ticketTypes, ticketCounts, typeLimits }) => {
  if (isDraftSlotGone || selectedSlot?.isSoldOut) return t("booking_widget.experience.availability.slot_unavailable")
  if (selectedSlot?.remaining != null && totalGuests > selectedSlot.remaining) {
    return t("booking_widget.experience.availability.places_now", { count: selectedSlot.remaining })
  }
  const shortType =
    typeLimits && ticketTypes.find((ticketType) => Number(ticketCounts?.[ticketType.id] || 0) > (typeLimits[ticketType.id] ?? Infinity))
  if (shortType) {
    return t("booking_widget.experience.availability.type_places_now", { count: typeLimits[shortType.id], title: shortType.title })
  }
  return null
}

// What a ticketed choice comes to against the start times on offer (useTicketedAvailability's
// daySlots):
// - selectedSlot: the chosen start time, while it is still on offer (else null);
// - ticketsChosen, and remaining: guests not yet given a ticket;
// - maxGuests: the booking's guest limit, lowered to the start time's places left once known;
// - typeLimits: places left per ticket type at that start time (null before the API says);
// - isDraftSlotGone: a start time was chosen but the day no longer offers it;
// - availabilityWarning: getAvailabilityWarning's text, or null.
export const getTicketedChoice = ({ t, experience, ticketTypes, ticketCounts, daySlots, scheduledSlot, date, totalGuests, isSlotsLoading }) => {
  const selectedSlot = daySlots.find((slot) => slot.id === scheduledSlot?.id) || null
  const ticketsChosen = countTickets(ticketTypes, ticketCounts)
  const slotPlaces = selectedSlot && !selectedSlot.isSoldOut ? selectedSlot.remaining : undefined
  const bookingLimit = getGuestLimitPerBooking(experience)
  const maxGuests = slotPlaces != null ? Math.min(bookingLimit || slotPlaces, slotPlaces) : bookingLimit
  const typeLimits = slotPlaces != null ? selectedSlot.remainingByType : null
  const isDraftSlotGone = !!scheduledSlot?.id && !!date && !isSlotsLoading && !selectedSlot
  const availabilityWarning = getAvailabilityWarning({ t, selectedSlot, isDraftSlotGone, totalGuests, ticketTypes, ticketCounts, typeLimits })
  return {
    selectedSlot,
    ticketsChosen,
    remaining: Math.max(0, totalGuests - ticketsChosen),
    maxGuests,
    typeLimits,
    isDraftSlotGone,
    availabilityWarning
  }
}
